import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';
import { Box, Typography, Card, CardContent, LinearProgress, Chip, CircularProgress, Icon } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import PageHeader from 'components/common/pageHeader';
import domainData from 'store/domainStore';
import { routes } from 'config';
import welcomeService from 'services/welcomeService';
import M from 'messages';
import wineListImportService from 'services/wineListImportService';
import classes from './styles';
import { gettingStartedSteps } from './gettingStartedConfig';
import { videoGuides } from './videoGuidesConfig';
import WineListImportModal from './modals/wineListImportModal';

const Home = observer(() => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = domainData.getCurrentUser();
  const [loading, setLoading] = useState({
    visits: true,
    winesCount: true,
    wineListsCount: true,
    subscription: true,
  });
  const [openImportWineModal, setOpenImportWineModal] = useState();
  const [stats, setStats] = useState({});

  const handleImportWineListButtonClick = () => {
    setOpenImportWineModal(true);
  };

  // Fetch visits independently
  const fetchVisits = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, visits: true }));
      const response = await welcomeService.getVisits();
      setStats(prev => ({ ...prev, visits: response?.data?.visits || 0 }));
    } catch (error) {
      enqueueSnackbar('Error fetching visits', { variant: 'error' });
      setStats(prev => ({ ...prev, visits: 0 }));
    } finally {
      setLoading(prev => ({ ...prev, visits: false }));
    }
  }, [enqueueSnackbar]);

  // Fetch wines count independently
  const fetchWinesCount = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, winesCount: true }));
      const response = await welcomeService.getWinesCount();
      setStats(prev => ({ ...prev, winesCount: response?.data?.count || 0 }));
    } catch (error) {
      enqueueSnackbar('Error fetching wines count', { variant: 'error' });
      setStats(prev => ({ ...prev, winesCount: 0 }));
    } finally {
      setLoading(prev => ({ ...prev, winesCount: false }));
    }
  }, [enqueueSnackbar]);

  // Fetch wine lists count independently
  const fetchWineListsCount = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, wineListsCount: true }));
      const response = await welcomeService.getWineListsCount();
      setStats(prev => ({ ...prev, wineListsCount: response?.data?.count || 0 }));
    } catch (error) {
      enqueueSnackbar('Error fetching wine lists count', { variant: 'error' });
      setStats(prev => ({ ...prev, wineListsCount: 0 }));
    } finally {
      setLoading(prev => ({ ...prev, wineListsCount: false }));
    }
  }, [enqueueSnackbar]);

  // Fetch subscription info independently
  const fetchSubscription = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, subscription: true }));
      const data = domainData.getSubscriptionAllAccess();
      setStats(prev => ({
        ...prev,
        exception: data.exception || false,
        winesLimit: data.wineCount,
        wineListsLimit: data.wineListCount,
        planName: data.planName || 'Free',
        unlimitedWine: data.unlimitedWine || false,
        unlimitedWineList: data.unlimitedWineList || false,
      }));
    } catch (error) {
      enqueueSnackbar('Error fetching subscription info', { variant: 'error' });
    } finally {
      setLoading(prev => ({ ...prev, subscription: false }));
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    const serviceProviderId = currentUser?.serviceProviderId;
    if (serviceProviderId) {
      // Fetch all data independently - each will load separately
      fetchVisits();
      fetchWinesCount();
      fetchWineListsCount();
    }
  }, [currentUser, fetchVisits, fetchWinesCount, fetchWineListsCount]);

  useEffect(() => {
    if (domainData.isSubscriptionAccessLoaded()) {
      fetchSubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainData.isSubscriptionAccessLoaded(), fetchSubscription]);

  const getWinesProgress = () => {
    if (stats.exception || stats.unlimitedWine) return 100;
    return Math.min((stats.winesCount / stats.winesLimit) * 100, 100);
  };

  const getWineListsProgress = () => {
    if (stats.exception || stats.unlimitedWineList) return 100;
    return Math.min((stats.wineListsCount / stats.wineListsLimit) * 100, 100);
  };

  const getWinesLimitText = () => {
    if (stats.exception || stats.unlimitedWine) return '∞';
    return stats.winesLimit;
  };

  const getWineListsLimitText = () => {
    if (stats.exception || stats.unlimitedWineList) return '∞';
    return stats.wineListsLimit;
  };
  const handleWineListImportSubmit = async payload => {
    await wineListImportService.requestWineListImport(payload);
  };

  return (
    <Box sx={classes.root}>
      <PageHeader generalPageTitle={M.get('welcome.pageTitle')} onlyGeneralPageTitle />

      {/* Welcome Section */}
      <Typography sx={classes.welcomeTitle}>{M.get('welcome.pageTitle')}</Typography>
      <Typography sx={classes.welcomeSubtitle} mt={1}>
        {M.get('welcome.subtitle')}
      </Typography>

      {/* Overview Statistics Section */}
      <Grid container spacing={2} sx={classes.statsGridContainer}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={classes.statCard}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Typography sx={classes.statLabel}>{M.get('welcome.stats.visits')}</Typography>
                <Box sx={classes.statIconBox}>
                  <Icon baseClassName="material-symbols-outlined" sx={classes.statIconColor}>
                    visibility
                  </Icon>
                </Box>
              </Box>
              {loading.visits ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Typography sx={classes.statValue}>{stats.visits}</Typography>
                  <Typography sx={classes.statSubtitle} mt={1}>
                    {M.get('welcome.stats.totalVisitors')}
                  </Typography>
                </>
              )}
              <Box display="flex" alignItems="center" mt={3}>
                <Icon
                  baseClassName="material-symbols-outlined"
                  sx={{ ...classes.crownIconColor, fontSize: '16px', mr: 0.5 }}>
                  crown
                </Icon>
                <Typography sx={classes.statUpgradeText}>{M.get('welcome.stats.viewDetailedAnalytics')}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={classes.statCard}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Typography sx={classes.statLabel}>{M.get('welcome.stats.winesAndSpirits')}</Typography>
                <Box sx={classes.statIconBox}>
                  <Icon baseClassName="material-symbols-outlined" sx={classes.statIconColor}>
                    wine_bar
                  </Icon>
                </Box>
              </Box>
              {loading.winesCount || loading.subscription ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Typography sx={classes.statValue}>
                    {stats.winesCount}{' '}
                    <span style={{ fontWeight: '500', color: '#6a7181' }}>/ {getWinesLimitText()}</span>
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={getWinesProgress()}
                    sx={{
                      ...classes.progressBar,
                      ...(stats.exception || stats.unlimitedWine
                        ? classes.progressBarUnlimited
                        : getWinesProgress() === 100
                          ? classes.progressBarFull
                          : classes.progressBarNormal),
                    }}
                  />
                  <Typography sx={classes.statSubtitle}>
                    {stats.exception
                      ? M.get('welcome.stats.exception')
                      : `${stats.planName} ${M.get('welcome.stats.plan')}`}
                  </Typography>
                </>
              )}
              <Box display="flex" alignItems="center" mt={1}>
                <Icon
                  baseClassName="material-symbols-outlined"
                  sx={{ ...classes.crownIconColor, fontSize: '16px', mr: 0.5 }}>
                  crown
                </Icon>
                <Typography sx={classes.statUpgradeText}>{M.get('welcome.stats.proUpgrade')}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={classes.statCard}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Typography sx={classes.statLabel}>{M.get('welcome.stats.wineLists')}</Typography>
                <Box sx={classes.statIconBox}>
                  <Icon baseClassName="material-symbols-outlined" sx={classes.statIconColor}>
                    checklist
                  </Icon>
                </Box>
              </Box>
              {loading.wineListsCount || loading.subscription ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Typography sx={classes.statValue}>
                    {stats.wineListsCount}{' '}
                    <span style={{ fontWeight: '500', color: '#6a7181' }}>/ {getWineListsLimitText()}</span>
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={getWineListsProgress()}
                    sx={{
                      ...classes.progressBar,
                      ...(stats.exception || stats.unlimitedWineList
                        ? classes.progressBarUnlimited
                        : getWineListsProgress() === 100
                          ? classes.progressBarFull
                          : classes.progressBarNormal),
                    }}
                  />
                  <Typography sx={classes.statSubtitle}>
                    {stats.exception
                      ? M.get('welcome.stats.exception')
                      : `${stats.planName} ${M.get('welcome.stats.plan')}`}
                  </Typography>
                </>
              )}
              <Box display="flex" alignItems="center" mt={1}>
                <Icon
                  baseClassName="material-symbols-outlined"
                  sx={{ ...classes.crownIconColor, fontSize: '16px', mr: 0.5 }}>
                  crown
                </Icon>
                <Typography sx={classes.statUpgradeText}>{M.get('welcome.stats.proUpgradeLists')}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={classes.statCard}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Typography sx={classes.statLabel}>{M.get('welcome.stats.wineListImport')}</Typography>
                <Box sx={classes.uploadIconBox}>
                  <Icon baseClassName="material-symbols-outlined" sx={classes.uploadIconColor}>
                    upload_file
                  </Icon>
                </Box>
              </Box>
              <Icon baseClassName="material-symbols-outlined" sx={classes.giftIconColor}>
                card_giftcard
              </Icon>
              <Chip size="small" label={M.get('welcome.import.freeImportIncluded')} sx={classes.freeImportChip} />
              <Typography sx={classes.statDescription} mt={1}>
                {M.get('welcome.import.description')}
              </Typography>
              <Box
                mt={2}
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={classes.viewAllLink}
                onClick={() => handleImportWineListButtonClick()}>
                {M.get('welcome.import.learnMore')}
                <Icon baseClassName="material-symbols-outlined" sx={classes.iconColorGray}>
                  arrow_forward
                </Icon>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions and What You Can Do Section - Same Row */}
      <Grid container spacing={2} sx={classes.quickActionsGridContainer}>
        {/* Quick Actions Section */}
        <Grid size={{ xs: 12, md: 4 }} sx={classes.quickActionsGrid}>
          <Typography sx={classes.sectionTitle} mb={2}>
            {M.get('welcome.quickActions.title')}
          </Typography>
          <Grid container spacing={1.5} sx={classes.quickActionsGridInner}>
            <Grid size={{ xs: 12 }} sx={{ flex: 1, display: 'flex' }}>
              <Card
                sx={{ ...classes.actionCardSmall, ...classes.quickActionCard }}
                onClick={() => handleImportWineListButtonClick()}>
                <CardContent sx={classes.quickActionCardContent}>
                  <Box sx={classes.quickActionBox}>
                    <Box sx={{ ...classes.iconContainer, ...classes.iconContainerGreen }}>
                      <Icon baseClassName="material-symbols-outlined" sx={classes.iconColorGreen}>
                        upload_file
                      </Icon>
                    </Box>
                    <Box flex={1} minWidth={0} alignItems="center">
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Typography sx={classes.actionTitleSmall}>
                          {M.get('welcome.quickActions.importWineList.title')}
                        </Typography>
                        <Chip
                          size="small"
                          label={M.get('welcome.quickActions.importWineList.new')}
                          sx={classes.newChip}
                        />
                      </Box>
                      <Typography sx={classes.actionDescriptionSmall} mt={0.5}>
                        {M.get('welcome.quickActions.importWineList.description')}
                      </Typography>
                    </Box>
                    <Icon baseClassName="material-symbols-outlined" sx={classes.arrowForwardIcon}>
                      arrow_forward
                    </Icon>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ flex: 1, display: 'flex' }}>
              <Card
                sx={{ ...classes.actionCardSmall, ...classes.quickActionCard }}
                onClick={() => navigate(routes.wines.path)}>
                <CardContent sx={classes.quickActionCardContent}>
                  <Box sx={classes.quickActionBox}>
                    <Box sx={{ ...classes.iconContainer, ...classes.iconContainerPink }}>
                      <Icon baseClassName="material-symbols-outlined" sx={classes.iconColorPink}>
                        search
                      </Icon>
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Typography sx={classes.actionTitleSmall}>
                          {M.get('welcome.quickActions.findOrCreateWine.title')}
                        </Typography>
                        <Chip
                          size="small"
                          label={M.get('welcome.quickActions.findOrCreateWine.new')}
                          sx={classes.newChip}
                        />
                      </Box>
                      <Typography sx={classes.actionDescriptionSmall} mt={0.5}>
                        {M.get('welcome.quickActions.findOrCreateWine.description')}
                      </Typography>
                    </Box>
                    <Icon baseClassName="material-symbols-outlined" sx={classes.arrowForwardIcon}>
                      arrow_forward
                    </Icon>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ flex: 1, display: 'flex' }}>
              <Card
                sx={{ ...classes.actionCardSmall, ...classes.quickActionCard }}
                onClick={() => {
                  if (currentUser?.serviceProviderId) {
                    navigate(routes.serviceProviderEdit.path.replace(':id', currentUser.serviceProviderId));
                  }
                }}>
                <CardContent sx={classes.quickActionCardContent}>
                  <Box sx={classes.quickActionBox}>
                    <Box sx={{ ...classes.iconContainer, ...classes.iconContainerGray }}>
                      <Icon baseClassName="material-symbols-outlined" sx={classes.iconColorGray}>
                        person
                      </Icon>
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Typography sx={classes.actionTitleSmall}>
                        {M.get('welcome.quickActions.personalizeProfile.title')}
                      </Typography>
                      <Typography sx={classes.actionDescriptionSmall} mt={0.5}>
                        {M.get('welcome.quickActions.personalizeProfile.description')}
                      </Typography>
                    </Box>
                    <Icon baseClassName="material-symbols-outlined" sx={classes.arrowForwardIcon}>
                      arrow_forward
                    </Icon>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* What You Can Do Section */}
        <Grid size={{ xs: 12, md: 8 }} sx={classes.quickActionsGrid}>
          <Typography sx={classes.sectionTitle} mb={2}>
            {M.get('welcome.features.title')}
          </Typography>
          <Grid container spacing={2} sx={classes.featuresGrid}>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
              <Card sx={{ ...classes.featureCard, ...classes.featureCardFlex }}>
                <CardContent sx={classes.featureCardContent}>
                  <Box>
                    <Box
                      sx={{
                        ...classes.iconContainer,
                        ...classes.featureIconContainerLarge,
                        ...classes.featureIconContainerPink,
                      }}>
                      <Icon baseClassName="material-symbols-outlined" sx={classes.iconColorPink}>
                        checklist
                      </Icon>
                    </Box>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography sx={classes.featureTitle}>
                        {M.get('welcome.features.digitalWineLists.title')}
                      </Typography>
                      <Chip
                        label={M.get('welcome.features.digitalWineLists.chip')}
                        size="small"
                        sx={classes.featureChip}
                      />
                    </Box>
                    <Typography sx={classes.featureDescription} mt={1}>
                      {M.get('welcome.features.digitalWineLists.description')}
                    </Typography>
                  </Box>
                  <Box sx={classes.featureLinkBox}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      sx={{ ...classes.viewAllLink, mt: 'auto' }}
                      onClick={e => {
                        e.preventDefault();
                        navigate(routes.wines.path);
                      }}>
                      {M.get('welcome.features.digitalWineLists.goTo')}
                      <Icon baseClassName="material-symbols-outlined" sx={classes.iconColorGray}>
                        arrow_forward
                      </Icon>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
              <Card sx={{ ...classes.featureCard, ...classes.featureCardFlex }}>
                <CardContent sx={classes.featureCardContent}>
                  <Box>
                    <Box
                      sx={{
                        ...classes.iconContainer,
                        ...classes.featureIconContainerLarge,
                        ...classes.featureIconContainerGold,
                      }}>
                      <Icon baseClassName="material-symbols-outlined" sx={classes.iconColorGold}>
                        verified_user
                      </Icon>
                    </Box>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography sx={classes.featureTitle}>{M.get('welcome.features.euWineLabels.title')}</Typography>
                      <Chip label={M.get('welcome.features.euWineLabels.chip')} size="small" sx={classes.featureChip} />
                    </Box>
                    <Typography sx={classes.featureDescription} mt={1}>
                      {M.get('welcome.features.euWineLabels.description')}
                    </Typography>
                    <Box mt={1} mb={1} sx={classes.freePlanCodeBox}>
                      <Icon baseClassName="material-symbols-outlined" sx={classes.iconColorGold}>
                        local_offer
                      </Icon>
                      <Typography sx={classes.freePlanCodeText}>
                        {M.get('welcome.features.euWineLabels.freePlanCode')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{ ...classes.viewAllLink, mt: 'auto' }}
                    onClick={() => {
                      navigate(routes.wineLabels.path);
                    }}>
                    {M.get('welcome.features.euWineLabels.goTo')}
                    <Icon baseClassName="material-symbols-outlined" sx={classes.iconColorGray}>
                      arrow_forward
                    </Icon>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Getting Started Section */}
      <Grid container spacing={2} sx={classes.gettingStartedGridContainer}>
        <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
          <Card sx={classes.gettingStartedCardWrapper}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Typography sx={classes.sectionTitle} mb={2}>
                {M.get('welcome.gettingStarted.title')}
              </Typography>
              <Grid container spacing={1.5}>
                {gettingStartedSteps.map(step => {
                  const handleStepClick = () => {
                    const route = step.getRoute(currentUser);
                    if (route) {
                      navigate(route);
                    }
                  };

                  return (
                    <Grid key={step.stepNumber} size={{ xs: 12 }}>
                      <Card
                        sx={{
                          ...classes.gettingStartedCard,
                          ...classes.gettingStartedCardHover,
                        }}
                        onClick={handleStepClick}>
                        <CardContent sx={classes.cardContentPadding}>
                          <Box display="flex" alignItems="center">
                            <Box sx={classes.stepNumberBox}>
                              <Typography sx={classes.stepNumberText}>{step.stepNumber}</Typography>
                            </Box>
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" gap={1} mb={0.5} sx={classes.stepIconContainer}>
                                <Icon baseClassName="material-symbols-outlined" sx={classes.stepIconColor}>
                                  {step.icon}
                                </Icon>
                                <Typography sx={classes.actionTitleSmall}>{step.title}</Typography>
                                {step.isPaid && (
                                  <Icon baseClassName="material-symbols-outlined" sx={classes.crownIconColor}>
                                    crown
                                  </Icon>
                                )}
                              </Box>
                              <Typography sx={classes.actionDescriptionSmall}>{step.description}</Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Video Guides Section */}
        <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
          <Card sx={classes.videoCardWrapper}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography sx={classes.sectionTitle}>{M.get('welcome.videoGuides.title')}</Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  sx={classes.viewAllLink}
                  onClick={() => window.open('https://sharevino.co/use-cases', '_blank')}>
                  {M.get('welcome.videoGuides.viewAll')}
                  <Icon baseClassName="material-symbols-outlined" sx={classes.iconColorGray}>
                    open_in_new
                  </Icon>
                </Box>
              </Box>
              <Grid container spacing={1.5}>
                {videoGuides.map((video, index) => (
                  <Grid key={index} size={{ xs: 12 }}>
                    <Card
                      sx={{
                        ...classes.videoCard,
                        ...classes.videoCardHover,
                      }}
                      onClick={() => window.open(video.url, '_blank')}>
                      <CardContent sx={classes.cardContentPadding}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" flex={1}>
                            <Box sx={classes.videoIconBox}>
                              <Icon baseClassName="material-symbols-outlined" sx={classes.iconColorPink}>
                                play_arrow
                              </Icon>
                            </Box>
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" gap={1.5}>
                                <Typography sx={classes.actionTitleSmall}>{video.title}</Typography>
                                {video.chipText && <Chip size="small" label={video.chipText} sx={classes.videoChip} />}
                              </Box>
                              <Typography sx={classes.actionDescriptionSmall} mt={0.5}>
                                {video.description}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography sx={classes.videoDuration} ml={1}>
                            {video.duration}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Need help? Section */}
      <Box sx={classes.helpSectionBox}>
        <Typography sx={classes.sectionTitle} mb={2}>
          {M.get('welcome.help.title')}
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              sx={classes.helpCard}
              onClick={() => {
                window.location.href = 'mailto:support@sharevino.co';
              }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={2} flex={1}>
                    <Box
                      sx={{ ...classes.iconContainer, ...classes.helpIconContainer, ...classes.helpIconContainerPink }}>
                      <Icon baseClassName="material-symbols-outlined" sx={classes.iconColorPink}>
                        email
                      </Icon>
                    </Box>
                    <Box>
                      <Typography sx={classes.actionTitleSmall}>{M.get('welcome.help.emailSupport.title')}</Typography>
                      <Typography sx={classes.actionDescriptionSmall}>
                        {M.get('welcome.help.emailSupport.email')}
                      </Typography>
                    </Box>
                  </Box>
                  <Icon baseClassName="material-symbols-outlined" sx={classes.helpArrowIcon}>
                    arrow_outward
                  </Icon>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              sx={classes.helpCard}
              onClick={() => {
                window.location.href = 'tel:+1(718) 300 8589';
              }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={2} flex={1}>
                    <Box
                      sx={{
                        ...classes.iconContainer,
                        ...classes.helpIconContainer,
                        ...classes.helpIconContainerGreen,
                      }}>
                      <Icon sx={classes.iconColorGreen}>settings_phone</Icon>
                    </Box>
                    <Box>
                      <Typography sx={classes.actionTitleSmall}>{M.get('welcome.help.whatsapp.title')}</Typography>
                      <Typography sx={classes.actionDescriptionSmall}>
                        {M.get('welcome.help.whatsapp.phone')}
                      </Typography>
                    </Box>
                  </Box>
                  <Icon baseClassName="material-symbols-outlined" sx={classes.helpArrowIcon}>
                    arrow_outward
                  </Icon>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              sx={classes.helpCard}
              onClick={() => {
                window.open('https://calendly.com/sharevinonyc/demo', '_blank');
              }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={2} flex={1}>
                    <Box
                      sx={{ ...classes.iconContainer, ...classes.helpIconContainer, ...classes.helpIconContainerGold }}>
                      <Icon sx={classes.iconColorGold}>calendar_today</Icon>
                    </Box>
                    <Box>
                      <Typography sx={classes.actionTitleSmall}>{M.get('welcome.help.bookDemo.title')}</Typography>
                      <Typography sx={classes.actionDescriptionSmall}>
                        {M.get('welcome.help.bookDemo.description')}
                      </Typography>
                    </Box>
                  </Box>
                  <Icon baseClassName="material-symbols-outlined" sx={classes.helpArrowIcon}>
                    arrow_outward
                  </Icon>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      <WineListImportModal
        open={openImportWineModal}
        handleClose={() => setOpenImportWineModal(false)}
        handleSubmit={handleWineListImportSubmit}
        serviceProviderId={currentUser?.serviceProviderId}
      />
    </Box>
  );
});

export default Home;
